import {
  type ApolloClient,
  type ApolloQueryResult,
  type NormalizedCacheObject,
  useApolloClient,
} from '@apollo/react-hooks';
import {
  AddCircleOutline,
  KeyboardDoubleArrowRight,
} from '@mui/icons-material';
import { Drawer, InputAdornment } from '@mui/material';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { number, object } from 'yup';
import {
  type AlertDetails,
  Button,
  Dialog,
  type DropDownSelectOption,
  IconButton,
  type SelectOption,
  TextField,
} from '../../../../../../components';
import { FundingSource } from '../../../../../Nomination/components/NominationForm/FundingModule/types';
import {
  type DimensionsCollection,
  PANEL_STATUS,
  type PanelApplication,
  type PanelFormData,
  type PanelNumbers,
  useAddApplicationToPanel,
  useAnnouncePanelResult,
  useGetApplicantInfo,
} from '../../../../graphql';
import { PANEL_FORM_DIALOG_TEXT, PANEL_LIST_TEXT } from '../../../../lang';
import {
  AddApplicationDialogContent,
  ApplicationList,
  MemoizedPanelSummaryTable,
  type ViewSituation,
} from './';
import {
  panelApplicantListPermissionConfig,
  panelFundingConfig,
} from './panelApplicantListPermissionConfig';
import { MemoizedPanelPartnerSummaryTable } from './PanelListTableCells/PanelPartnerSummaryTable';
import {
  MemoizedPanelTagFilter,
  type PanelAppliedFilter,
  type PanelLockedFilter,
} from './PanelTagFilter';

type PanelApplicationsProps = {
  viewSituation: ViewSituation;
  panelId: string;
  panelApplications: PanelApplication[];
  panelNumbers: PanelNumbers;
  dualPartnerUniversityList: SelectOption[];
  panelInformation: PanelFormData;
  dimensionsCollection: DimensionsCollection[];
  allocationReleased: boolean;
  hasAllocatedReviewers: boolean;
  appliedFilter: PanelAppliedFilter;
  lockedFilter?: PanelLockedFilter;
  filterLockedFlag: boolean;
  gsAutoRefresh?: boolean;
  handleApplyTagFilter: (value: PanelAppliedFilter) => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
  handleManageAlert: (alertDetail: AlertDetails) => void;
  refetch: () => Promise<ApolloQueryResult<any>>;
};

type ApplicantDialogConfirm = {
  applicantSearch: number | '';
  applicationId?: string;
  applicantName?: string;
};

const autoRefreshFre: number =
  Number(process.env.REACT_APP_PANEL_LIST_AUTO_REFRESH_FRE) ?? 30000;

export const PanelApplications: React.FunctionComponent<
  PanelApplicationsProps
> = (props: PanelApplicationsProps) => {
  const {
    viewSituation,
    panelApplications: wholeData,
    panelId,
    panelNumbers,
    panelInformation,
    dimensionsCollection,
    allocationReleased,
    hasAllocatedReviewers,
    appliedFilter,
    gsAutoRefresh = false,
    filterLockedFlag,
    dualPartnerUniversityList,
    lockedFilter,
    handleApplyTagFilter = () => {},
    handleLoading,
    handleManageAlert,
    refetch,
  } = props;
  const [confirmDialogOpen, setConfirmPanelDialogOpen] =
    useState<boolean>(false);
  const [lockOn, setLockOn] = useState<boolean>(filterLockedFlag);

  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [tableData, setTableData] = useState<PanelApplication[]>(wholeData);
  const [summaryTableOpen, setSummaryTableOpen] = useState(false);
  const [partnerSummaryTableOpen, setPartnerSummaryTableOpen] = useState(false);
  const [triggerRender, setTriggerRender] = useState(0);
  const [dialogDetails, setDialogDetails] = useState<{
    content: React.ReactNode;
    handleDialogConfirmAction: any;
  }>({
    content: '',
    handleDialogConfirmAction: () => {},
  });

  useEffect(() => {
    // works when table data refetch
    const regex = new RegExp(searchKeyword, 'i'); // i: case insensitive
    const filteredResults = wholeData.filter(item =>
      regex.test(JSON.stringify(item)),
    );
    setTableData(filteredResults);
  }, [wholeData]);

  useEffect(() => {
    setLockOn(filterLockedFlag);
  }, [filterLockedFlag]);

  const { title, fundingSource, endDate, tagOptions, status, resultOptions } =
    panelInformation;
  const isHKPFS =
    fundingSource === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME;
  const { showDeptPriorityNo, showPartnerUniversity } = panelFundingConfig(
    fundingSource as FundingSource,
  );

  const {
    panelDetailNumber,
    panelShortlistNumber,
    panelShortlistOutstandingNumber,
    panel3YearNumber,
    panel2YearNumber,
    panel1YearNumber,
    panelWithdrawnNumber,
    panelFailedNumber,
    totalStudentshipYears,
  } = panelNumbers;

  const dropdownTagOptions: DropDownSelectOption[] = tagOptions.map(op => ({
    value: op,
    label: op,
  }));

  const {
    enableAutoRefresh,
    enableFilter,
    enableSubmit,
    enableAddApplicant,
    enableSendLockedFilter,
    hideNumberSummary,
  } = panelApplicantListPermissionConfig[viewSituation];

  const client = useApolloClient() as ApolloClient<NormalizedCacheObject>;
  const handleOpenApplicantConfirmDialog = async (
    values: ApplicantDialogConfirm,
  ): Promise<void> => {
    const { applicantSearch } = values;
    const hasExist = wholeData.some(
      applicant => applicant.applicationNo === applicantSearch.toString(),
    );

    if (hasExist) {
      applicantSearchFormik.setFieldError('applicantSearch', 'Already exists');
      return;
    }

    await handleLoading(async () => {
      try {
        const applicantInfo = await useGetApplicantInfo(
          client,
          applicantSearch.toString(),
        );
        if (!(applicantInfo?._id ?? '')) {
          applicantSearchFormik.setFieldError(
            'applicantSearch',
            'No match found',
          );
          return;
        }

        const {
          _id,
          applicant: { name },
        } = applicantInfo;
        const applicantName = `${name?.surname} ${name?.givenName}`;
        await applicantSearchFormik.setValues({
          ...applicantSearchFormik.values,
          applicationId: _id,
          applicantName,
        });
        setDialogDetails(returnDialogDetails('addApplication', applicantName));
        setConfirmPanelDialogOpen(true);
      } catch (e) {
        handleManageAlert({
          alertContent: `Error Occurs.`,
          alertType: 'error',
        });
      }
    });
  };

  const applicantSearchFormik = useFormik<ApplicantDialogConfirm>({
    initialValues: { applicantSearch: '' },
    validationSchema: object({
      applicantSearch: number(),
    }),
    onSubmit: handleOpenApplicantConfirmDialog,
  });

  const panelBasicInfo: Array<{ label: string; value: string }> = [
    {
      label: PANEL_LIST_TEXT.TITLE,
      value: title,
    },
    {
      label: PANEL_LIST_TEXT.MEETING_DATE,
      value: dayjs(endDate).format('DD/MM/YYYY'),
    },
  ];

  const panelDetailInfo: Array<{ label: string; value: string[] }> = [
    {
      label: PANEL_LIST_TEXT.APPLICATIONS_NUMBER,
      value: [
        `${panelDetailNumber.totalNumber} (S: ${panelDetailNumber.scienceNumber} H: ${panelDetailNumber.humanityNumber})`,
      ],
    },
    {
      label: PANEL_LIST_TEXT.PANEL_RESULT_SUCCESSFULL,
      value: [
        `Shortlisted: ${panelShortlistNumber.totalNumber} (S: ${panelShortlistNumber.scienceNumber} H: ${panelShortlistNumber.humanityNumber})`,
        `Shortlisted (Outstanding): ${panelShortlistOutstandingNumber.totalNumber} (S: ${panelShortlistOutstandingNumber.scienceNumber} H: ${panelShortlistOutstandingNumber.humanityNumber})`,
        `3yrs from Central: ${panel3YearNumber.totalNumber} (S: ${panel3YearNumber.scienceNumber} H: ${panel3YearNumber.humanityNumber})`,
        `2yrs from Central: ${panel2YearNumber.totalNumber} (S: ${panel2YearNumber.scienceNumber} H: ${panel2YearNumber.humanityNumber})`,
        `1yrs from Central: ${panel1YearNumber.totalNumber} (S: ${panel1YearNumber.scienceNumber} H: ${panel1YearNumber.humanityNumber})`,
      ],
    },
    {
      label: PANEL_LIST_TEXT.PANEL_RESULT,
      value: [
        `Failed: ${panelFailedNumber} | Withdrawn: ${panelWithdrawnNumber}`,
      ],
    },
    {
      label: PANEL_LIST_TEXT.STUDENTSHIP_YEARS_GIVEN_BY_PANEL,
      value: [`${totalStudentshipYears}`],
    },
  ];

  const addApplicationsToPanel = useAddApplicationToPanel();
  const handleAddApplicationToPanel = async (): Promise<void> => {
    const { applicationId, applicantSearch } = applicantSearchFormik.values;
    await handleLoading(async () => {
      try {
        const variables = {
          input: {
            panelId,
            applicationId: [applicationId],
          },
        };
        await addApplicationsToPanel({
          variables,
        });

        await applicantSearchFormik.setFieldValue('applicantSearch', '');
        await refetch();
        handleManageAlert({
          alertContent: `Successfully added the applicant(${applicantSearch}) to the panel list.`,
          alertType: 'success',
        });
      } catch (error: any) {
        handleManageAlert({
          alertContent: 'Error Occurs.' + error.message,
          alertType: 'error',
        });
      }
    });
    setConfirmPanelDialogOpen(false);
  };

  const announcePanelResult = useAnnouncePanelResult();
  const handleAnnouncePanelResult = async (): Promise<void> => {
    await handleLoading(async () => {
      try {
        const variables = {
          panelId,
        };
        await announcePanelResult({
          variables,
        });
        handleManageAlert({
          alertContent: PANEL_LIST_TEXT.SUBMITTED,
          alertType: 'success',
        });

        await refetch();
      } catch (error: any) {
        handleManageAlert({
          alertContent: 'Error Occurs.' + error.message,
          alertType: 'error',
        });
      }
    });
    setConfirmPanelDialogOpen(false);
  };
  useEffect(() => {
    if (enableAutoRefresh || gsAutoRefresh) {
      // auto refresh every `autoRefreshFre` ms
      const interval = setInterval(() => {
        void refetch();
        setTriggerRender(prev => prev + 1);
      }, autoRefreshFre);

      return () => {
        clearInterval(interval);
      };
    }
  }, [refetch, enableAutoRefresh, gsAutoRefresh]);

  const applicantSearchInput = useMemo((): React.ReactNode => {
    const {
      touched,
      errors,
      values: formikVal,
      handleChange,
      handleBlur,
    } = applicantSearchFormik;
    return (
      <>
        <form noValidate onSubmit={applicantSearchFormik.handleSubmit}>
          <TextField
            size="small"
            className="w-[20rem]"
            name="applicantSearch"
            type="number"
            placeholder="Please Input Application No."
            value={formikVal.applicantSearch}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.applicantSearch && Boolean(errors.applicantSearch)}
            helperText={touched.applicantSearch && errors.applicantSearch}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    icon={<AddCircleOutline />}
                    type="submit"
                    disabled={!formikVal.applicantSearch}
                    color="primary"
                  />
                </InputAdornment>
              ),
            }}
          />
        </form>
      </>
    );
  }, [applicantSearchFormik]);

  const returnDialogDetails = (
    situation: 'submitPanel' | 'addApplication',
    applicantName?: string,
  ): {
    content: React.ReactNode;
    handleDialogConfirmAction: any;
  } => {
    // should not read applicantName from applicantSearchFormik directly, due to rendering delays,
    const { applicantSearch } = applicantSearchFormik.values;

    switch (situation) {
      case 'submitPanel':
        return {
          content: (
            <div className="bg-white border border-[#DFDFDF] mx-4 flex flex-col justify-center items-center p-4">
              {PANEL_FORM_DIALOG_TEXT.ANNOUNCE}
            </div>
          ),
          handleDialogConfirmAction: handleAnnouncePanelResult,
        };

      case 'addApplication':
        return {
          content: (
            <AddApplicationDialogContent
              applicantSearch={applicantSearch}
              applicantName={applicantName ?? ''}
            />
          ),
          handleDialogConfirmAction: handleAddApplicationToPanel,
        };

      default:
        return {
          content: '',
          handleDialogConfirmAction: () => {},
        };
    }
  };

  const fuzzySearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const keyword = event.target.value;
    setSearchKeyword(keyword);
    const regex = new RegExp(keyword, 'i'); // i: case insenstive
    const filteredResults = wholeData.filter(item =>
      regex.test(JSON.stringify(item)),
    );
    setTableData(filteredResults);
  };

  const handleToggleDrawer = (
    event: React.KeyboardEvent | React.MouseEvent,
  ): void => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setSummaryTableOpen(false);
  };

  const handleTogglePartnerDrawer = (
    event: React.KeyboardEvent | React.MouseEvent,
  ): void => {
    if (
      event.type === 'keydown' &&
      ((event as React.KeyboardEvent).key === 'Tab' ||
        (event as React.KeyboardEvent).key === 'Shift')
    ) {
      return;
    }

    setPartnerSummaryTableOpen(false);
  };

  const handleSetLock = (lock: boolean): void => {
    setLockOn(lock);
  };
  const isReadyToAnnounce =
    (status === PANEL_STATUS.PREPARED || status === PANEL_STATUS.IN_PROGRESS) &&
    hasAllocatedReviewers &&
    allocationReleased;
  const panelSubmitted = status === PANEL_STATUS.COMPLETED;

  const isAllowedToAdd =
    (!allocationReleased || !isHKPFS) && enableAddApplicant;

  return (
    <div>
      <Dialog
        sx={{
          bgcolor: '#FAFAFA',
        }}
        openDialog={confirmDialogOpen}
        dialogTitle={''}
        dialogAction={PANEL_FORM_DIALOG_TEXT.CONFIRM}
        cancelAction={PANEL_FORM_DIALOG_TEXT.CANCEL}
        onDialogAction={dialogDetails.handleDialogConfirmAction}
        width="680px"
        onDialogClose={() => {
          setConfirmPanelDialogOpen(false);
        }}
      >
        {dialogDetails.content}
      </Dialog>

      <>
        {/* basic information about this round of panel */}
        <div className="w-full border border-black rounded py-4 px-8 mb-2">
          <div className="flex justify-around gap-[20px] py-2 text-sm">
            {panelBasicInfo.map((items, index) => {
              return (
                <div key={index} className="flex flex-row">
                  <p className="font-bold">
                    {items.label}
                    {':'}&nbsp;&nbsp;
                  </p>
                  {items.value}
                </div>
              );
            })}
          </div>
        </div>

        {/*  insert tag filter */}
        {enableFilter && (
          <MemoizedPanelTagFilter
            panelId={panelId}
            panelResultOptions={resultOptions}
            dualPartnerUniversityList={dualPartnerUniversityList}
            appliedFilter={appliedFilter}
            showDeptPriorityNo={showDeptPriorityNo}
            showPartnerUniversity={showPartnerUniversity}
            // if gs member open auto refresh, unable to lock filter
            // unexpected scenarios: GS helper A (opened auto-refresh) locks the filter, GS helper B(didn't open auto-refresh) would never notice
            enableSendLockedFilter={enableSendLockedFilter && !gsAutoRefresh}
            fundingSource={fundingSource}
            dropdownTagOptions={dropdownTagOptions}
            lockOn={lockOn}
            handleSetLock={handleSetLock}
            lockedFilter={lockedFilter}
            handleApplyTagFilter={handleApplyTagFilter}
            handleLoading={handleLoading}
            handleManageAlert={handleManageAlert}
            refetch={refetch}
          />
        )}

        {/*
         * add panel member, hide if
         * 1. reviewer allocation released
         * 2. in panel member view
         */}
        {isAllowedToAdd && (
          <div className="flex flex-row px-2 py-4 w-full gap-2 items-start rounded-sm">
            <p className="mt-[0.3rem] font-semibold text-md text-[#951D1D]">
              {PANEL_FORM_DIALOG_TEXT.ADD_APPLICANT}
            </p>
            {applicantSearchInput}
          </div>
        )}

        {/* panel number list */}
        {!hideNumberSummary && (
          <div className="bg-[#F6F6F6] p-2 mb-4">
            <div
              className={`grid grid-cols-${panelDetailInfo.length} gap-[4px] py-2 text-sm justify-items-center`}
            >
              {panelDetailInfo.map((items, index) => {
                return (
                  <div key={index} className="text-center text-[#951D1D]">
                    <p className="font-semibold mb-2">{items.label}</p>
                    {items.value.map((item, idx) => {
                      return <p key={`${idx}-${item}`}>{item}</p>;
                    })}
                    {/* <p>{items.value}</p> */}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* only gs member can do the final submit, disable the button when
         * 1. has not allocated reviewers
         * 2. though allocated reviewers, reviewers haven't been released
         * 3. panel result has been announced ( completed )
         */}

        {enableSubmit && (
          <div className="w-full flex justify-center">
            <span>
              <Button
                variant="contained"
                sx={{ textTransform: 'none' }}
                disabled={!isReadyToAnnounce && isHKPFS}
                title={panelSubmitted ? PANEL_LIST_TEXT.SUBMITTED : ''}
                onClick={() => {
                  setDialogDetails(returnDialogDetails('submitPanel'));
                  setConfirmPanelDialogOpen(true);
                }}
              >
                {PANEL_LIST_TEXT.SUBMIT}
              </Button>
            </span>
          </div>
        )}

        <div className="flex flex-row justify-between items-center">
          {/* left side: summary table */}
          <div>
            {!hideNumberSummary && (
              <React.Fragment key={'right'}>
                <Button
                  variant="contained"
                  sx={{
                    borderRadius: '20px',
                    '& .MuiButton-endIcon': {
                      transition: 'transform 0.3s ease',
                      transform: summaryTableOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    },
                  }}
                  endIcon={<KeyboardDoubleArrowRight />}
                  onClick={() => {
                    setSummaryTableOpen(!summaryTableOpen);
                  }}
                >
                  {PANEL_FORM_DIALOG_TEXT.SUMMARY_TABLE}
                </Button>

                <Drawer
                  anchor={'right'}
                  open={summaryTableOpen}
                  onClose={handleToggleDrawer}
                >
                  <MemoizedPanelSummaryTable
                    panelResultOptions={resultOptions as DropDownSelectOption[]}
                    panelId={panelId}
                  />
                </Drawer>

                {showPartnerUniversity && (
                  <>
                    <Button
                      variant="contained"
                      sx={{
                        borderRadius: '20px',
                        '& .MuiButton-endIcon': {
                          transition: 'transform 0.3s ease',
                          transform: partnerSummaryTableOpen
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        },
                      }}
                      endIcon={<KeyboardDoubleArrowRight />}
                      onClick={() => {
                        setPartnerSummaryTableOpen(!partnerSummaryTableOpen);
                      }}
                    >
                      {PANEL_FORM_DIALOG_TEXT.PARTNER_SUMMARY_TABLE}
                    </Button>
                    <Drawer
                      anchor={'right'}
                      open={partnerSummaryTableOpen}
                      onClose={handleTogglePartnerDrawer}
                    >
                      <MemoizedPanelPartnerSummaryTable
                        panelResultOptions={
                          resultOptions as DropDownSelectOption[]
                        }
                        panelId={panelId}
                      />
                    </Drawer>
                  </>
                )}
              </React.Fragment>
            )}
          </div>
          {/* right side: search bar */}
          <div className="m-[10px] flex flex-row items-center justify-end gap-[10px]">
            <TextField
              onChange={fuzzySearch}
              placeholder="Search..."
              value={searchKeyword}
            />
            <p className="w-[120px] text-right">
              Total Count: {tableData.length}
            </p>
          </div>
        </div>

        <ApplicationList
          key={wholeData.length}
          triggerRender={triggerRender + Number(filterLockedFlag) + 100}
          panelId={panelId}
          panelSubmitted={panelSubmitted}
          viewSituation={viewSituation}
          tagOptions={tagOptions}
          panelResultOptions={resultOptions}
          dimensionsCollection={dimensionsCollection}
          fundingSourceEnum={fundingSource}
          panelApplications={tableData}
          handleLoading={handleLoading}
          refetch={refetch}
        />
      </>
    </div>
  );
};
